import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Box, Button, TextField, Typography, Alert, FormControlLabel, Checkbox } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { ClientFormData } from '../../types';
import { clientsAPI } from '../../services/api';
import { NAVIGATION, VALIDATION_MESSAGES } from '../../constants';
import { useClient } from '../../hooks/useClients';
import FileUpload from '../../components/files/FileUpload';
import { useFiles } from '../../hooks/useFiles';

const schema = z.object({
  name: z.string().min(2, VALIDATION_MESSAGES.MIN_LENGTH(2)),
  email: z.string().email(VALIDATION_MESSAGES.EMAIL).optional(),
  phone: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  zipCode: z.string().optional(),
  country: z.string().optional(),
  company: z.string().optional(),
  isDomestic: z.boolean().optional(),
  pib: z.string().optional(),
  registrationNumber: z.string().optional(),
  startDate: z.string().optional(),
  terminationDate: z.string().optional(),
});

const EditClient: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { client, isLoading: clientLoading } = useClient(id!);
  const { files, uploadFile, deleteFile } = useFiles('CLIENT', id!);
  
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset, watch, control } = useForm<ClientFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      zipCode: '',
      country: '',
      company: '',
      isDomestic: false,
      pib: '',
      registrationNumber: '',
      startDate: '',
      terminationDate: '',
    }
  });
  const [success, setSuccess] = React.useState('');
  const [error, setError] = React.useState('');
  const [originalData, setOriginalData] = React.useState<ClientFormData | null>(null);
  
  const watchedValues = watch();
  const hasChanges = originalData && Object.keys(watchedValues).some(key => {
    const currentValue = watchedValues[key as keyof ClientFormData];
    const originalValue = originalData[key as keyof ClientFormData];
    return currentValue !== originalValue;
  });

  useEffect(() => {
    if (client) {
      const formData = {
        name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        city: client.city || '',
        zipCode: client.zipCode || '',
        country: client.country || '',
        company: client.company || '',
        isDomestic: client.isDomestic || false,
        pib: client.pib || '',
        registrationNumber: client.registrationNumber || '',
        startDate: client.startDate ? client.startDate.substring(0, 10) : '',
        terminationDate: client.terminationDate ? client.terminationDate.substring(0, 10) : '',
      };
      reset(formData);
      setOriginalData(formData);
    }
  }, [client, reset]);

  const onSubmit = async (data: ClientFormData) => {
    setSuccess(''); setError('');
    try {
      const payload: any = {
        name: data.name,
        startDate: data.startDate ? new Date(data.startDate).toISOString() : undefined,
        terminationDate: data.terminationDate ? new Date(data.terminationDate).toISOString() : undefined,
      };
      if (data.email) payload.email = data.email;
      if (data.phone) payload.phone = data.phone;
      if (data.address) payload.address = data.address;
      if (data.city) payload.city = data.city;
      if (data.zipCode) payload.zipCode = data.zipCode;
      if (data.country) payload.country = data.country;
      if (data.company) payload.company = data.company;
      if (data.isDomestic !== undefined) payload.isDomestic = data.isDomestic;
      if (data.pib) payload.pib = data.pib;
      if (data.registrationNumber) payload.registrationNumber = data.registrationNumber;
      
      await clientsAPI.update(id!, payload);
      setSuccess('Client updated successfully!');
      navigate(NAVIGATION.CLIENTS);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update client');
    }
  };

  if (clientLoading) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, px: { xs: 2, md: 0 } }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, px: { xs: 2, md: 0 } }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Edit Client</Typography>
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField label="Name" fullWidth margin="normal" {...register('name')} error={!!errors.name} helperText={errors.name?.message} InputLabelProps={{ shrink: true }} />
            <TextField label="Company" fullWidth margin="normal" {...register('company')} error={!!errors.company} helperText={errors.company?.message} InputLabelProps={{ shrink: true }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField label="Email" fullWidth margin="normal" {...register('email')} error={!!errors.email} helperText={errors.email?.message} InputLabelProps={{ shrink: true }} />
            <TextField label="Phone" fullWidth margin="normal" {...register('phone')} error={!!errors.phone} helperText={errors.phone?.message} InputLabelProps={{ shrink: true }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField label="Address" fullWidth margin="normal" {...register('address')} error={!!errors.address} helperText={errors.address?.message} InputLabelProps={{ shrink: true }} />
            <TextField label="Zip Code" fullWidth margin="normal" {...register('zipCode')} error={!!errors.zipCode} helperText={errors.zipCode?.message} InputLabelProps={{ shrink: true }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField label="City" fullWidth margin="normal" {...register('city')} error={!!errors.city} helperText={errors.city?.message} InputLabelProps={{ shrink: true }} />
            <TextField label="Country" fullWidth margin="normal" {...register('country')} error={!!errors.country} helperText={errors.country?.message} InputLabelProps={{ shrink: true }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField label="Start Date" type="date" fullWidth margin="normal" {...register('startDate')} InputLabelProps={{ shrink: true }} error={!!errors.startDate} helperText={errors.startDate?.message} />
            <TextField label="Termination Date" type="date" fullWidth margin="normal" {...register('terminationDate')} InputLabelProps={{ shrink: true }} error={!!errors.terminationDate} helperText={errors.terminationDate?.message} />
          </Box>
          
          {/* Domestic Client Section */}
          <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <FormControlLabel
              control={
                <Controller
                  name="isDomestic"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={field.value || false}
                    />
                  )}
                />
              }
              label="Domestic Client (Serbian company)"
            />
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mt: 2 }}>
              <TextField 
                label="PIB (Tax ID)" 
                fullWidth 
                margin="normal" 
                {...register('pib')} 
                error={!!errors.pib} 
                helperText={errors.pib?.message}
                disabled={!watch('isDomestic')}
                InputLabelProps={{ shrink: true }}
              />
              <TextField 
                label="Registration Number" 
                fullWidth 
                margin="normal" 
                {...register('registrationNumber')} 
                error={!!errors.registrationNumber} 
                helperText={errors.registrationNumber?.message}
                disabled={!watch('isDomestic')}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" mb={2}>Files</Typography>
          <Box sx={{ width: '100%', overflow: 'auto' }}>
            <FileUpload
              entityType="CLIENT"
              entityId={id!}
              files={files}
              uploadFile={uploadFile}
              deleteFile={deleteFile}
              onFilesChange={() => {}} // Files are managed by useFiles hook
            />
          </Box>
        </Box>

        <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }} disabled={isSubmitting || !hasChanges}>
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </form>
    </Box>
  );
};

export default EditClient; 